import type { TreeData, TreeNode, TreeLink } from '@/api/tree.api';

// ─── Erkundungsansicht: nach und nach aufdecken ──────────────────────────────
//
// Ein Stammbaum mit achthundert Personen ist als Ganzes kein Bild mehr, sondern
// eine Tapete. Man sieht alles und erkennt nichts; die Person, die man sucht,
// steht irgendwo zwischen zweihundert anderen Kästchen.
//
// Diese Ansicht dreht es um: Es beginnt mit EINER Person und ihren nächsten
// Angehörigen. Jeder Knoten trägt eine Zahl – so viele direkte Verwandte gibt
// es noch, die gerade nicht zu sehen sind. Ein Klick darauf holt sie dazu. Wer
// eine Linie verfolgen will, klickt sich an ihr entlang, und der Rest der
// Familie bleibt aus dem Weg.
//
// Sie ist AUSDRÜCKLICH nichts, was von selbst angeht. Wer seinen Baum kennt und
// ihn ganz sehen will, soll ihn ganz sehen – auch mit achthundert Personen. Die
// Anwendung darf einem gepflegten Stammbaum nicht unterstellen, er sei zu groß.
//
// Der Kern steht hier und nicht in der Ansicht: was sichtbar ist und wie viele
// Verwandte hinter einem Knoten warten, ist eine Frage an Daten und lässt sich
// ohne Browser beantworten.

/** Ab wie vielen Personen die Ansicht überhaupt vorgeschlagen wird. */
export const VIELE_PERSONEN = 150;

export interface AufdeckErgebnis {
  /** Der Ausschnitt, den die Ansicht zeichnet. */
  sichtbar: TreeData;
  /**
   * Je sichtbarer Person: wie viele ihrer direkten Verwandten fehlen noch.
   * Personen ohne verdeckte Verwandte stehen nicht darin.
   */
  verdeckt: Map<string, number>;
}

/** Eltern, Kinder und Partner einer Person – die direkten Nachbarn im Baum. */
export function direkteVerwandte(daten: TreeData, personId: string): string[] {
  const raus = new Set<string>();

  for (const n of daten.nodes) {
    if (n.id === personId) {
      if (n.primaryParentId) raus.add(n.primaryParentId);
      if (n.secondaryParentId) raus.add(n.secondaryParentId);
    } else if (n.primaryParentId === personId || n.secondaryParentId === personId) {
      raus.add(n.id);
    }
  }

  for (const l of daten.links) {
    if (l.person1Id === personId) raus.add(l.person2Id);
    if (l.person2Id === personId) raus.add(l.person1Id);
  }

  raus.delete(personId);
  return [...raus];
}

/**
 * Womit die Erkundung beginnt: eine Person und ihre nächsten Angehörigen.
 *
 * Nur die Wurzel allein wäre ein einzelnes Kästchen auf leerer Fläche – das
 * sieht nach einem Fehler aus, nicht nach einem Anfang.
 */
export function anfang(daten: TreeData, wurzelId: string): Set<string> {
  const start = new Set<string>([wurzelId]);
  for (const id of direkteVerwandte(daten, wurzelId)) start.add(id);
  return start;
}

/**
 * Wer sichtbar ist – und wie viele Verwandte hinter jedem noch warten.
 *
 * Zwei Dinge müssen dabei sauber gekappt werden, sonst zeichnet das Layout ins
 * Leere:
 *
 *   1. Ein Elternverweis auf eine verdeckte Person. Er bliebe eine Linie zu
 *      einem Kästchen, das es auf dieser Fläche nicht gibt.
 *   2. Eine Ehe, von der nur ein Partner sichtbar ist. Sie ist eine Verbindung
 *      zwischen zweien; mit einem ist sie keine.
 */
export function deckeAuf(daten: TreeData, aufgedeckt: ReadonlySet<string>): AufdeckErgebnis {
  const sichtbareIds = new Set<string>();
  for (const n of daten.nodes) if (aufgedeckt.has(n.id)) sichtbareIds.add(n.id);

  const nodes: TreeNode[] = daten.nodes
    .filter((n) => sichtbareIds.has(n.id))
    .map((n) => ({
      ...n,
      primaryParentId:
        n.primaryParentId && sichtbareIds.has(n.primaryParentId) ? n.primaryParentId : null,
      secondaryParentId:
        n.secondaryParentId && sichtbareIds.has(n.secondaryParentId) ? n.secondaryParentId : null,
    }));

  const links: TreeLink[] = daten.links.filter(
    (l) => sichtbareIds.has(l.person1Id) && sichtbareIds.has(l.person2Id),
  );

  const verdeckt = new Map<string, number>();
  for (const id of sichtbareIds) {
    const fehlend = direkteVerwandte(daten, id).filter((v) => !sichtbareIds.has(v)).length;
    if (fehlend > 0) verdeckt.set(id, fehlend);
  }

  return { sichtbar: { nodes, links }, verdeckt };
}

/**
 * Die Verwandten einer Person dazunehmen.
 *
 * Gibt eine NEUE Menge zurück, statt die alte zu ändern: der Zustand liegt im
 * Store, und React erkennt eine veränderte Menge sonst nicht als neu.
 */
export function decke(
  daten: TreeData,
  aufgedeckt: ReadonlySet<string>,
  personId: string,
): Set<string> {
  const neu = new Set(aufgedeckt);
  for (const id of direkteVerwandte(daten, personId)) neu.add(id);
  return neu;
}

/**
 * Eine Person und ihre gesamte Ahnenreihe: Eltern, deren Eltern und so weiter.
 *
 * Bewusst NUR aufwärts. Geschwister, Onkel und Tanten, Kinder und Partner
 * kämen auf jeder Stufe dazu und machten aus einer Linie wieder die Tapete,
 * vor der die Erkundung schützen soll. Ehen zwischen aufgedeckten Eltern
 * zeichnet `deckeAuf` von selbst.
 *
 * Der Besuchtenmenge wegen endet die Suche auch bei fehlerhaften Daten mit
 * einem Kreis (jemand ist sein eigener Vorfahr); sie läuft nicht endlos.
 */
export function vorfahren(daten: TreeData, personId: string): Set<string> {
  const nachId = new Map<string, TreeNode>();
  for (const n of daten.nodes) nachId.set(n.id, n);

  const raus = new Set<string>();
  const offen = [personId];
  while (offen.length > 0) {
    const id = offen.pop()!;
    const n = nachId.get(id);
    if (!n || raus.has(id)) continue;
    raus.add(id);
    if (n.primaryParentId) offen.push(n.primaryParentId);
    if (n.secondaryParentId) offen.push(n.secondaryParentId);
  }
  return raus;
}

/**
 * Personen im Baum nach Namen finden – ohne Server, die Daten liegen schon da.
 *
 * Jedes Wort der Eingabe muss im Namen vorkommen (Groß- und Kleinschreibung
 * egal), in beliebiger Reihenfolge: „maria müller" findet „Müller, Maria".
 * Treffer, die mit der Eingabe BEGINNEN, stehen vorn.
 */
export function suchePerson(daten: TreeData, eingabe: string, limit = 8): TreeNode[] {
  const woerter = eingabe.toLocaleLowerCase('de').split(/\s+/).filter(Boolean);
  if (woerter.length === 0) return [];

  const treffer: Array<{ n: TreeNode; anfang: boolean }> = [];
  for (const n of daten.nodes) {
    const name = `${n.firstName} ${n.lastName ?? ''}`.toLocaleLowerCase('de');
    if (!woerter.every((w) => name.includes(w))) continue;
    const umgekehrt = `${n.lastName ?? ''} ${n.firstName}`.toLocaleLowerCase('de');
    treffer.push({
      n,
      anfang: name.startsWith(woerter[0]) || umgekehrt.startsWith(woerter[0]),
    });
  }
  // sort ist stabil: innerhalb der Gruppen bleibt die Reihenfolge der Daten.
  treffer.sort((a, b) => Number(b.anfang) - Number(a.anfang));
  return treffer.slice(0, limit).map((t) => t.n);
}

/**
 * Eine brauchbare Wurzel, wenn keine Person ausgewählt ist.
 *
 * Genommen wird, wer die meisten direkten Verwandten hat – von dort aus führen
 * die meisten Wege weiter. Bei Gleichstand entscheidet die Reihenfolge der
 * Daten, damit dieselbe Eingabe dieselbe Wurzel ergibt und der Baum nicht bei
 * jedem Laden woanders aufmacht.
 */
export function vorschlagWurzel(daten: TreeData): string | null {
  if (!daten.nodes.length) return null;

  const grad = new Map<string, number>();
  const zaehle = (id: string) => grad.set(id, (grad.get(id) ?? 0) + 1);

  for (const n of daten.nodes) {
    if (n.primaryParentId) {
      zaehle(n.id);
      zaehle(n.primaryParentId);
    }
    if (n.secondaryParentId) {
      zaehle(n.id);
      zaehle(n.secondaryParentId);
    }
  }
  for (const l of daten.links) {
    zaehle(l.person1Id);
    zaehle(l.person2Id);
  }

  let beste = daten.nodes[0].id;
  let bester = grad.get(beste) ?? 0;
  for (const n of daten.nodes) {
    const g = grad.get(n.id) ?? 0;
    if (g > bester) {
      beste = n.id;
      bester = g;
    }
  }
  return beste;
}
